/**
 * Underlying Compare strip (internal name: options gutter): a right-docked
 * strip with three fixed lanes (ES | SPX | SPY), each drawing a compact
 * per-strike histogram of that underlying's option book (fill = OI
 * distribution, Exposure profile, or Options volume) vertically aligned with
 * the main chart's price axis. The chart crosshair scrubs history: hovering a
 * candle snaps all three lanes to the book as of that time (same at-or-before
 * row selection the single-underlying Exposure Profile uses); no crosshair
 * shows the latest row of the loaded range. Live spot ticks draw only in the
 * live resting state (no crosshair, range ending at the current session).
 *
 * The gutter is a DOM sibling of the chart host (NOT a chart primitive): it
 * cannot affect autoscale, and lightweight-charts will not repaint it, so a
 * rAF loop repaints only when a cheap fingerprint changes (priceToCoordinate
 * of the data extremes captures pan/zoom/autoscale in two numbers). Lanes are
 * normalized independently: units and book sizes differ across underlyings,
 * so cross-lane magnitude comparison would be a units trap.
 */

import { useEffect, useRef, useState } from 'react';
import {
  useUnderlyingCompareData, COMPARE_UNDERLYINGS,
  selectExposureRow, selectVolumeRow, selectOiSession, laneRatioFor,
} from '../hooks/useUnderlyingCompareData';
import { getLatestSessionDate, stepWeekday } from '../utils/market';
import { formatTime } from '../utils/formatters';

// Strip width: user-resizable by dragging the left edge, persisted globally
// (layout pref like hideGridLines, deliberately NOT per-page indicator state).
const DEFAULT_WIDTH = 234;
const MIN_WIDTH = 120;
const MAX_WIDTH = 600;
const HANDLE_W = 6;
const WIDTH_STORAGE_KEY = 'underlyingCompareWidth';

const HEADER_H = 18;
const LANE_PAD = 5;

const LANE_ACCENT = { es: '#f59e0b', spx: '#a855f7', spy: '#06b6d4' };
const LANE_LABEL = { es: 'ES', spx: 'SPX', spy: 'SPY' };
const CALL_COLOR = '#4ade80';
const PUT_COLOR = '#f87171';
// Sign-keyed per-greek palette, byte-matching the main-chart Exposure
// Profile defaults (GreeksHeatmapPlugin _getOiBarColor) so a wall reads the
// same color in the strip and on the chart. Width encodes magnitude.
const GREEK_SIGN_COLOR = {
  gex: { pos: 'rgba(80, 165, 255, 0.9)', neg: 'rgba(255, 155, 50, 0.9)' },
  dex: { pos: 'rgba(255, 95, 95, 0.9)', neg: 'rgba(60, 220, 120, 0.9)' },
  vex: { pos: 'rgba(195, 120, 255, 0.9)', neg: 'rgba(255, 215, 65, 0.9)' },
  cex: { pos: 'rgba(40, 210, 240, 0.9)', neg: 'rgba(245, 60, 100, 0.9)' },
};
// Zero Levels line colors + label glyphs, byte-matching the main-chart
// layer (GreeksHeatmapPlugin levelConfig), NOT the exposure palette above,
// so a crossing reads the same color and glyph in the strip and on the
// chart. The Gamma Levels colors likewise mirror the plugin's GVWAP/wall
// styling.
const ZERO_LEVEL_COLOR = {
  gex: 'rgba(0, 255, 200, 0.9)',
  dex: 'rgba(59, 130, 246, 0.9)',
  vex: 'rgba(168, 85, 247, 0.9)',
  cex: 'rgba(6, 182, 212, 0.9)',
};
const ZERO_LEVEL_LABEL = { gex: 'γ0', dex: 'δ0', vex: 'ν0', cex: 'χ0' };
const ZERO_GREEKS = ['gex', 'dex', 'vex', 'cex'];
const GAMMA_GVWAP_LINE = 'rgba(217, 119, 6, 0.95)';
const GAMMA_WALL_LINE = 'rgba(180, 83, 9, 0.85)';

const BG = '#1a1a1a';
const SEPARATOR = '#2a2a2a';
const TEXT = '#d1d5db';
const MUTED = '#6b7280';
const HOVER_GUIDE = '#758696';

function formatNative(u, v) {
  if (v == null || !isFinite(v)) return '';
  return u === 'spx' ? v.toFixed(1) : v.toFixed(2);
}

function clampWidth(w) {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(w)));
}

function readStoredWidth() {
  try {
    const v = parseFloat(localStorage.getItem(WIDTH_STORAGE_KEY));
    if (isFinite(v)) return clampWidth(v);
  } catch { /* ignore */ }
  return DEFAULT_WIDTH;
}

function persistWidth(w) {
  try { localStorage.setItem(WIDTH_STORAGE_KEY, String(w)); } catch { /* ignore */ }
}

export default function UnderlyingCompare({
  series, chart, mode, greek, expiryMode, timeframe,
  date, endDate, timezone = 'ET', sessionRatiosRef,
  zeroLevels = false, gammaLevels = false,
}) {
  const { lanesRef, versionRef } = useUnderlyingCompareData({
    mode, expiryMode, timeframe, date, endDate, sessionRatiosRef,
    wantZero: zeroLevels, wantGamma: gammaLevels,
  });
  const canvasRef = useRef(null);
  const hoverYRef = useRef(null);
  const crosshairTimeRef = useRef(null);
  const lastFpRef = useRef(null);
  const selectionRef = useRef({ key: null, perLane: null });

  // Left-edge drag resize. The strip is a plain DOM sibling of the chart, so
  // no capture-phase chart-container machinery is needed (nothing competes
  // with lightweight-charts pan here). The paint loop polls clientWidth into
  // its fingerprint every frame, so a width change repaints on its own.
  const [width, setWidth] = useState(readStoredWidth);
  const [handleHover, setHandleHover] = useState(false);
  const dragRef = useRef(null);
  const lastUpRef = useRef(null);

  const onHandleDown = (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    dragRef.current = { pointerId: e.pointerId, startX: e.clientX, startWidth: width, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
  };
  const onHandleMove = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = d.startX - e.clientX;
    if (!d.moved && Math.abs(dx) <= 3) return;
    d.moved = true;
    setWidth(clampWidth(d.startWidth + dx));
  };
  const onHandleUp = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    if (d.moved) {
      // Real drag: persist the final position once, on release only.
      lastUpRef.current = null;
      persistWidth(clampWidth(d.startWidth + (d.startX - e.clientX)));
      return;
    }
    // Click without movement. Two clicks within 400ms and ~4px = reset to the
    // default width. Detected manually: pointer capture retargets events, so
    // native click/dblclick composition is unreliable here (same pattern as
    // the profile-axis anchor drag).
    const now = performance.now();
    const prev = lastUpRef.current;
    if (e.type === 'pointerup' && prev && (now - prev.t) < 400
        && Math.abs(e.clientX - prev.x) < 4 && Math.abs(e.clientY - prev.y) < 4) {
      lastUpRef.current = null;
      setWidth(DEFAULT_WIDTH);
      persistWidth(DEFAULT_WIDTH);
      return;
    }
    lastUpRef.current = e.type === 'pointerup' ? { t: now, x: e.clientX, y: e.clientY } : null;
  };

  useEffect(() => {
    if (!series || !chart) return undefined;
    let rafId = null;
    let disposed = false;

    // Belt-and-suspenders with the rAF fingerprint: repaint immediately on
    // pan/zoom instead of waiting for the next frame's diff.
    const onRangeChange = () => { lastFpRef.current = null; };
    try { chart.timeScale().subscribeVisibleLogicalRangeChange(onRangeChange); } catch { /* chart mid-teardown */ }

    // Chart crosshair = the history scrubber. param.time is the hovered bar's
    // time (on renko: the de-collided display second, ~= the real second,
    // the same tolerance the plugin's own scrub accepts).
    const onCrosshair = (param) => {
      crosshairTimeRef.current = (param.point && param.time != null) ? param.time : null;
    };
    try { chart.subscribeCrosshairMove(onCrosshair); } catch { /* chart mid-teardown */ }

    const esSpot = () => {
      try {
        const data = series.data();
        if (!data || !data.length) return null;
        const last = data[data.length - 1];
        return last.close ?? last.value ?? null;
      } catch {
        return null;
      }
    };

    // Row selection is cached per (crosshair time, data version, mode): the
    // 60fps rAF tick must not reverse-scan every session every frame.
    const currentSelection = () => {
      const t = crosshairTimeRef.current;
      const key = `${t}|${versionRef.current}|${mode}`;
      if (selectionRef.current.key === key) return selectionRef.current.perLane;
      const perLane = {};
      for (const u of COMPARE_UNDERLYINGS) {
        const lane = lanesRef.current[u];
        if (mode === 'oi') {
          const { dateKey, promoted } = selectOiSession(lane.oiSessions, t);
          perLane[u] = { bars: lane.oiSessions?.[dateKey]?.bars || null, dateKey, promoted };
        } else if (mode === 'exposure') {
          perLane[u] = selectExposureRow(lane, t);
        } else {
          perLane[u] = selectVolumeRow(lane, t);
        }
      }
      selectionRef.current = { key, perLane };
      return perLane;
    };

    const laneValues = (bars) => {
      if (mode === 'exposure') {
        let max = 0;
        for (const b of bars) max = Math.max(max, Math.abs(b[greek] || 0));
        return { max, signed: true };
      }
      const ck = mode === 'volume' ? 'call_vol' : 'call_oi';
      const pk = mode === 'volume' ? 'put_vol' : 'put_oi';
      let max = 0;
      for (const b of bars) max = Math.max(max, b[ck] || 0, b[pk] || 0);
      return { max, signed: false, ck, pk };
    };

    const paint = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      if (!cssW || !cssH) return;
      const dpr = window.devicePixelRatio || 1;

      const t = crosshairTimeRef.current;
      const liveEdge = t == null && endDate === getLatestSessionDate();
      const selection = currentSelection();

      // Fingerprint: two projected prices capture pan/zoom/autoscale; the
      // rest captures data, scrub position, size, hover and settings.
      let yLo = null;
      let yHi = null;
      let pLo = Infinity;
      let pHi = -Infinity;
      for (const u of COMPARE_UNDERLYINGS) {
        for (const b of selection[u].bars || []) {
          if (b.price < pLo) pLo = b.price;
          if (b.price > pHi) pHi = b.price;
        }
      }
      try {
        if (isFinite(pLo)) { yLo = series.priceToCoordinate(pLo); yHi = series.priceToCoordinate(pHi); }
      } catch {
        return;
      }
      const spotEs = liveEdge ? esSpot() : null;
      const fp = `${yLo}|${yHi}|${cssW}|${cssH}|${dpr}|${versionRef.current}|${hoverYRef.current}|${t}|${mode}|${greek}|${spotEs}|${liveEdge}|${zeroLevels}|${gammaLevels}`;
      if (fp === lastFpRef.current) return;
      lastFpRef.current = fp;

      const bw = Math.round(cssW * dpr);
      const bh = Math.round(cssH * dpr);
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
      }
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, cssW, cssH);

      const laneW = cssW / COMPARE_UNDERLYINGS.length;
      ctx.font = '10px monospace';

      COMPARE_UNDERLYINGS.forEach((u, i) => {
        const lane = lanesRef.current[u];
        const bars = selection[u].bars || [];
        const x0 = i * laneW;
        const cx = x0 + laneW / 2;

        if (i > 0) {
          ctx.strokeStyle = SEPARATOR;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(Math.round(x0) + 0.5, 0);
          ctx.lineTo(Math.round(x0) + 0.5, cssH);
          ctx.stroke();
        }

        const { max, signed, ck, pk } = laneValues(bars);
        const halfMax = laneW / 2 - LANE_PAD;

        if (!bars.length || !(max > 0)) {
          ctx.fillStyle = MUTED;
          ctx.textAlign = 'center';
          ctx.fillText('no data', cx, cssH / 2);
        } else {
          // Fixed thickness matching the main-chart Exposure Profile bars
          // (GreeksHeatmapPlugin barHeight = 4 physical px). This ctx is
          // dpr-transformed (CSS px), the plugin draws in bitmap px, so divide.
          const thickness = Math.max(1, 4 / dpr);

          let minY = Infinity;
          let maxY = -Infinity;
          for (const b of bars) {
            const y = series.priceToCoordinate(b.price);
            if (y == null || y < HEADER_H || y > cssH) continue;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
            const yTop = y - thickness / 2;
            if (signed) {
              const v = b[greek] || 0;
              const w = (v / max) * halfMax;
              const pair = GREEK_SIGN_COLOR[greek] || GREEK_SIGN_COLOR.gex;
              ctx.fillStyle = w >= 0 ? pair.pos : pair.neg;
              if (w >= 0) ctx.fillRect(cx, yTop, Math.max(w, 0.5), thickness);
              else ctx.fillRect(cx + w, yTop, -w, thickness);
            } else {
              const cw = ((b[ck] || 0) / max) * halfMax;
              const pw = ((b[pk] || 0) / max) * halfMax;
              if (cw > 0) { ctx.fillStyle = CALL_COLOR; ctx.fillRect(cx, yTop, Math.max(cw, 0.5), thickness); }
              if (pw > 0) { ctx.fillStyle = PUT_COLOR; ctx.fillRect(cx - pw, yTop, Math.max(pw, 0.5), thickness); }
            }
          }

          if (isFinite(minY)) {
            ctx.strokeStyle = SEPARATOR;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(Math.round(cx) + 0.5, minY);
            ctx.lineTo(Math.round(cx) + 0.5, maxY);
            ctx.stroke();
          }
        }

        // Zero / Gamma Levels: the lane's own underlying's baked per-day
        // levels for the SAME session the fill shows (selection dateKey;
        // endDate at rest). Prices are ES points server-side, straight
        // through priceToCoordinate. Absent day = draw nothing (levels
        // from another session at another spot would be a lie). Small
        // right-edge label chips; everything clips to [HEADER_H, cssH].
        const levelKey = selection[u].dateKey || (t == null ? endDate : null);
        if (levelKey && (zeroLevels || gammaLevels)) {
          const labels = [];
          const zl = zeroLevels ? lane.zeroLevels?.[levelKey]?.zero_levels : null;
          if (zl) {
            ctx.lineWidth = 1;
            ctx.setLineDash([3, 3]);
            for (const g of ZERO_GREEKS) {
              ctx.strokeStyle = ZERO_LEVEL_COLOR[g];
              for (const lv of (zl[g] || [])) {
                if (lv?.price == null) continue;
                const ly = series.priceToCoordinate(lv.price);
                if (ly == null || ly < HEADER_H || ly > cssH) continue;
                ctx.beginPath();
                ctx.moveTo(x0 + 2, Math.round(ly) + 0.5);
                ctx.lineTo(x0 + laneW - 2, Math.round(ly) + 0.5);
                ctx.stroke();
                labels.push({ y: Math.round(ly) + 0.5, text: ZERO_LEVEL_LABEL[g], color: ZERO_LEVEL_COLOR[g] });
              }
            }
            ctx.setLineDash([]);
          }
          const gl = gammaLevels ? lane.gammaLevels?.[levelKey]?.gamma_levels : null;
          if (gl) {
            if (gl.gvwap?.price != null) {
              const gy = series.priceToCoordinate(gl.gvwap.price);
              if (gy != null && gy >= HEADER_H && gy <= cssH) {
                ctx.strokeStyle = GAMMA_GVWAP_LINE;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x0 + 2, Math.round(gy) + 0.5);
                ctx.lineTo(x0 + laneW - 2, Math.round(gy) + 0.5);
                ctx.stroke();
                labels.push({ y: Math.round(gy) + 0.5, text: 'GV', color: GAMMA_GVWAP_LINE });
              }
            }
            if (Array.isArray(gl.walls)) {
              ctx.strokeStyle = GAMMA_WALL_LINE;
              ctx.lineWidth = 1;
              ctx.setLineDash([3, 3]);
              gl.walls.forEach((w, wi) => {
                if (w?.price == null) return;
                const wy = series.priceToCoordinate(w.price);
                if (wy == null || wy < HEADER_H || wy > cssH) return;
                ctx.beginPath();
                ctx.moveTo(x0 + 2, Math.round(wy) + 0.5);
                ctx.lineTo(x0 + laneW - 2, Math.round(wy) + 0.5);
                ctx.stroke();
                labels.push({ y: Math.round(wy) + 0.5, text: `GW${wi + 1}`, color: GAMMA_WALL_LINE });
              });
              ctx.setLineDash([]);
            }
          }
          // Small labels, right edge of the lane: 9px chips on a dark
          // backing. Zero crossings cluster, so entries within 11px merge
          // into one row (deduped by text), the strip's version of the
          // main chart's 3pt label grouping.
          if (labels.length) {
            ctx.font = '9px monospace';
            ctx.textAlign = 'left';
            labels.sort((a, b) => a.y - b.y);
            let li = 0;
            while (li < labels.length) {
              const cluster = [labels[li]];
              let lj = li + 1;
              while (lj < labels.length && labels[lj].y - cluster[cluster.length - 1].y <= 11) {
                cluster.push(labels[lj]);
                lj += 1;
              }
              const seen = new Set();
              const uniq = cluster.filter((e) => !seen.has(e.text) && seen.add(e.text));
              const widths = uniq.map((e) => ctx.measureText(e.text).width);
              const total = widths.reduce((a, b) => a + b, 0) + (uniq.length - 1) * 4;
              const cy = cluster[0].y;
              let tx = x0 + laneW - 4 - total;
              ctx.fillStyle = 'rgba(26, 26, 26, 0.9)';
              ctx.fillRect(tx - 2, cy - 6, total + 5, 12);
              uniq.forEach((e, k) => {
                ctx.fillStyle = e.color;
                ctx.fillText(e.text, tx, cy + 3);
                tx += widths[k] + 4;
              });
              li = lj;
            }
            ctx.font = '10px monospace';
          }
        }

        // Spot tick, live resting state only: a live index quote against a
        // historic/scrubbed book would be a lie. Within-lane geometry is
        // exact because the lane's strikes and its spot go through the SAME
        // session anchor; the vertical spread between the three ticks is the
        // live cash-futures divergence.
        if (liveEdge) {
          const ratio = laneRatioFor(u, lane, endDate, sessionRatiosRef?.current);
          const laneSpotEs = u === 'es'
            ? spotEs
            : (lane.spot != null && ratio > 0 ? lane.spot * ratio : null);
          if (laneSpotEs != null) {
            const sy = series.priceToCoordinate(laneSpotEs);
            if (sy != null && sy >= HEADER_H && sy <= cssH) {
              ctx.strokeStyle = LANE_ACCENT[u];
              ctx.lineWidth = 1;
              ctx.setLineDash([3, 3]);
              ctx.beginPath();
              ctx.moveTo(x0 + 2, Math.round(sy) + 0.5);
              ctx.lineTo(x0 + laneW - 2, Math.round(sy) + 0.5);
              ctx.stroke();
              ctx.setLineDash([]);
            }
          }
        }
      });

      // Header band last so bars never bleed through the labels.
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, cssW, HEADER_H);
      ctx.strokeStyle = SEPARATOR;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, HEADER_H + 0.5);
      ctx.lineTo(cssW, HEADER_H + 0.5);
      ctx.stroke();
      COMPARE_UNDERLYINGS.forEach((u, i) => {
        ctx.fillStyle = LANE_ACCENT[u];
        ctx.textAlign = 'center';
        ctx.fillText(LANE_LABEL[u], i * laneW + laneW / 2, 12);
      });

      // Footer: which moment (and which book) the lanes show. Sits over the
      // strip's bottom edge (level with the chart's time axis, where no bars
      // draw). A T+1 book must never read as today's exposure, so the label
      // draws whenever a T+1 row is displayed, scrubbing or not.
      const t1Lane = COMPARE_UNDERLYINGS.map((u) => selection[u]).find((s) => s.isT1);
      const promotedLane = COMPARE_UNDERLYINGS.map((u) => selection[u]).find((s) => s.promoted);
      if (t != null || t1Lane || promotedLane) {
        let label;
        if (t1Lane) {
          label = `T+1 ${stepWeekday(t1Lane.t1Key, 1)}`;
          if (t != null) label += ` as of ${formatTime(t, timezone)}`;
        } else if (t == null && promotedLane) {
          label = `${promotedLane.dateKey} next session`;
        } else {
          const selDate = COMPARE_UNDERLYINGS.map((u) => selection[u].dateKey).find(Boolean);
          label = `${selDate && selDate !== endDate ? selDate + ' ' : ''}as of ${formatTime(t, timezone)}`;
        }
        const tw = ctx.measureText(label).width + 10;
        ctx.fillStyle = 'rgba(26, 26, 26, 0.9)';
        ctx.fillRect(cssW / 2 - tw / 2, cssH - 16, tw, 14);
        ctx.fillStyle = TEXT;
        ctx.textAlign = 'center';
        ctx.fillText(label, cssW / 2, cssH - 5);
      }

      // Hover: guide line + per-lane NATIVE price readout (ES / SPX / SPY),
      // converted through the HOVERED session's anchor.
      const hy = hoverYRef.current;
      if (hy != null && hy > HEADER_H && hy < cssH) {
        let esP = null;
        try { esP = series.coordinateToPrice(hy); } catch { /* below the main pane */ }
        ctx.strokeStyle = HOVER_GUIDE;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(0, Math.round(hy) + 0.5);
        ctx.lineTo(cssW, Math.round(hy) + 0.5);
        ctx.stroke();
        ctx.setLineDash([]);
        if (esP != null) {
          COMPARE_UNDERLYINGS.forEach((u, i) => {
            const lane = lanesRef.current[u];
            const ratio = laneRatioFor(u, lane, selection[u].dateKey || endDate, sessionRatiosRef?.current);
            const native = u === 'es' ? esP : (ratio > 0 ? esP / ratio : null);
            const label = formatNative(u, native);
            if (!label) return;
            const cx = i * laneW + laneW / 2;
            const ty = hy - 6 > HEADER_H + 10 ? hy - 6 : hy + 12;
            const tw = ctx.measureText(label).width + 6;
            ctx.fillStyle = 'rgba(26, 26, 26, 0.9)';
            ctx.fillRect(cx - tw / 2, ty - 9, tw, 12);
            ctx.fillStyle = TEXT;
            ctx.textAlign = 'center';
            ctx.fillText(label, cx, ty);
          });
        }
      }
    };

    const tick = () => {
      if (disposed) return;
      paint();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      disposed = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      try { chart.timeScale().unsubscribeVisibleLogicalRangeChange(onRangeChange); } catch { /* chart already removed */ }
      try { chart.unsubscribeCrosshairMove(onCrosshair); } catch { /* chart already removed */ }
    };
  }, [series, chart, mode, greek, endDate, timezone, lanesRef, versionRef, sessionRatiosRef, zeroLevels, gammaLevels]);

  return (
    <div
      style={{
        flex: `0 0 ${width}px`,
        width,
        height: '100%',
        background: BG,
        borderLeft: `1px solid ${SEPARATOR}`,
        position: 'relative',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          hoverYRef.current = e.clientY - rect.top;
        }}
        onMouseLeave={() => { hoverYRef.current = null; }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: HANDLE_W,
          cursor: 'ew-resize',
          touchAction: 'none',
          background: (handleHover || dragRef.current)
            ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
        }}
        onPointerDown={onHandleDown}
        onPointerMove={onHandleMove}
        onPointerUp={onHandleUp}
        onPointerCancel={onHandleUp}
        onPointerEnter={() => setHandleHover(true)}
        onPointerLeave={() => setHandleHover(false)}
      />
    </div>
  );
}
